import { useEffect, useRef, type ReactNode } from "react";
import { WebDialog } from "../../common/WebDialog.web";
import type { ControlSurfaceProps } from "./SettingsControlParts.types";

// A detail dialog can open above Settings. Keep keyboard focus in the active
// layer and return it to its row when closed; alerts remain above this layer.
function DetailFocusScope({
  children,
  onClose,
}: {
  children: ReactNode;
  onClose: () => void;
}) {
  const content = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const node = content.current;
    const dialog = node?.closest<HTMLElement>('[role="dialog"]');
    const overlay = dialog?.parentElement;
    if (!node || !dialog || !overlay) return;
    const trigger = document.activeElement as HTMLElement | null;
    const returnSection = trigger?.closest("section");
    const siblings = Array.from(document.body.children).filter(
      (el): el is HTMLElement =>
        el instanceof HTMLElement && el !== overlay && !el.contains(overlay),
    );
    const previous = siblings.map((el) => ({ el, inert: el.inert }));
    previous.forEach(({ el }) => {
      el.inert = true;
    });
    node.focus();
    const onKey = (event: KeyboardEvent) => {
      if (document.querySelector('[role="alertdialog"]')) return;
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== "Tab") return;
      const elements = Array.from(
        dialog.querySelectorAll<HTMLElement>(
          'button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), [tabindex="0"]',
        ),
      ).filter((el) => el.getClientRects().length > 0);
      const first = elements[0];
      const last = elements[elements.length - 1];
      if (!first) {
        event.preventDefault();
        node.focus();
        return;
      }
      if (!dialog.contains(document.activeElement)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
        return;
      }
      if (
        event.shiftKey &&
        (document.activeElement === first || document.activeElement === node)
      ) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      previous.forEach(({ el, inert }) => {
        el.inert = inert;
      });
      if (trigger?.isConnected && !trigger.closest("[inert]")) trigger.focus();
      else if (returnSection?.isConnected)
        returnSection
          .querySelector<HTMLElement>("button:not(:disabled)")
          ?.focus();
    };
  }, [onClose]);
  return (
    <div
      ref={content}
      tabIndex={-1}
      style={{
        outline: "none",
        display: "flex",
        flexDirection: "column",
        gap: 20,
      }}
    >
      {children}
    </div>
  );
}

export function SettingsDetailSurface({
  visible,
  title,
  onClose,
  onAfterClose,
  children,
  footer,
}: ControlSurfaceProps) {
  const wasVisible = useRef(false);
  useEffect(() => {
    if (wasVisible.current && !visible) onAfterClose?.();
    wasVisible.current = visible;
  }, [visible, onAfterClose]);
  return (
    <div onKeyDown={(event) => event.stopPropagation()}>
      <WebDialog
        visible={visible}
        title={title}
        wrapTitle
        onClose={onClose}
        maxWidth={540}
        zIndex={1100}
        footer={footer}
      >
        <DetailFocusScope onClose={onClose}>{children}</DetailFocusScope>
      </WebDialog>
    </div>
  );
}
